import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { message, notification } from "antd";
import { extractEtfsFromPortfolio } from "../SmaV2/smaHelpers";
import useSma from "../SmaV2/useSma";
import { useTickerSearchIndex } from "../../modules/Search/useTickerSearchIndex";
import { setExistingPortfolioBuilderContext } from "../../store/slices/builder.slice";
import { loadSmaFromConfig, initializeSmaWithTemplate } from "../../store/slices/sma.slice";
import { fetchTickerData } from "../../api/tickersApi";
import { SCREENER_FIELDS } from "../../constants";
import {
    updateUserPortfolioData,
    updatePortfolioDetails,
    fetchDetailedPortfolioObjects
} from "../../api/portfolioApi";
import {createNewEtfMatrix} from "../../store/slices/appState.slice";

const usePortfolioActions = ({ portfolio, prefix }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { initializeWithTemplate } = useSma(); // Have to keep this at top level

    const [currentAction, setCurrentAction] = useState(null);
    const [fetchComplete, setFetchComplete] = useState(false);
    const [fetchedPortfolioId, setFetchedPortfolioId] = useState(null);

    // Memoize the portfolio selector
    const fullPortfolioObject = useSelector(
        state => state.portfolios.items.find(p => p.id === fetchedPortfolioId),
        [fetchedPortfolioId]
    );

    // Memoize symbols array
    const symbols = useMemo(() => 
        fullPortfolioObject?.symbols ? Object.keys(fullPortfolioObject.symbols) : [],
        [fullPortfolioObject?.symbols]
    );

    const { tickers } = useTickerSearchIndex(symbols);

    // Memoize fetch function
    const fetchPortfolio = useCallback((portfolioId) => {
        dispatch(fetchDetailedPortfolioObjects([portfolioId])).then(() => {
            setFetchComplete(true);
        });
    }, [dispatch]);

    /** Handles updating positions (tickers/sleeves) directly  */
    const handleUpdatePositions = useCallback(() => {
        dispatch(setExistingPortfolioBuilderContext(fullPortfolioObject));
        navigate("/builders/portfolio");
    }, [dispatch, navigate, fullPortfolioObject]);

    const handleEtfMatrix = useCallback(() => {
        const etfs = extractEtfsFromPortfolio(fullPortfolioObject?.symbols, tickers);
        if (!etfs) return;

        dispatch(createNewEtfMatrix(etfs.map(e => e.symbol)));
        navigate('/etfs/matrix')
    })

    /** Handles generating new SMA portfolio and linking to etf portfolio */
    const handleCreateSma = useCallback(() => {
        console.log(fullPortfolioObject);
        const etfs = extractEtfsFromPortfolio(fullPortfolioObject?.symbols, tickers);
        console.log(etfs);
        if (!etfs) return;
        initializeWithTemplate(etfs, fullPortfolioObject?.id);
        const url = prefix ? `${prefix}/builders/sma?view=preview` : "/builders/sma?view=preview";
        navigate(url);
    }, [fullPortfolioObject, tickers]);

    /** Handles editing an existing SMA portfolio */
    const handleEditSma = useCallback(() => {
        const smaConfigId = `sma_${fullPortfolioObject?.id}`;  // Create a distinct SMA ID
        // const smaConfigId = fullPortfolioObject.id;
        const smaConfig = fullPortfolioObject?.properties?.sma_config;
        if (!smaConfig) return;
        
        const etfSymbols = Object.keys(smaConfig.etfs);
        dispatch(loadSmaFromConfig({ smaConfigId, smaConfig: {...smaConfig} }));
        dispatch(fetchTickerData({
            symbols: etfSymbols,
            fields: SCREENER_FIELDS,
            collection: 'US'
        }));
        navigate("/builders/sma?view=table");
    }, [fullPortfolioObject]);


    // Star/Favorite action


    const handlePortfolioAction = useCallback((action) => {
        setCurrentAction(action);
        setFetchedPortfolioId(portfolio.id);
        fetchPortfolio(portfolio.id);
    }, [portfolio?.id, fetchPortfolio]);

    // Effect to handle actions after fetch completes
    useEffect(() => {
        if (!fetchComplete || !fullPortfolioObject) return;
        
        setFetchComplete(false);

        const actionHandlers = {
            updatePositions: handleUpdatePositions,
            createSma: handleCreateSma,
            editSma: handleEditSma,
            etfMatrix: handleEtfMatrix
        };

        const handler = actionHandlers[currentAction];
        if (handler) {
            handler();
        }

    }, [
        fetchComplete, 
        fullPortfolioObject, 
        currentAction, 
        handleUpdatePositions,
        handleCreateSma,
        handleEditSma
    ]);

    return {
        handlePortfolioAction,
        currentAction,
        setCurrentAction,
        fullPortfolioObject,
        handleEtfMatrix

    };
};

export default usePortfolioActions;